/**
 * Commission & Referral System
 * 
 * Komisyon Yapısı:
 * - Alıcıdan: %5 (BUYER_COMMISSION_RATE)
 * - Satıcıdan: %5 (SELLER_COMMISSION_RATE)
 * - Toplam Platform Komisyonu: %10
 * 
 * Referans Ödülü:
 * - Platform komisyonunun %2'si referans sahibine gider
 * - Örnek: 1000 TL satış → 100 TL komisyon → 20 TL referans, 80 TL platform
 */

// Platform commission rates (split between buyer and seller) - DEFAULTS ONLY
// Actual rates should be passed from useSystemConfig()
export const DEFAULT_BUYER_COMMISSION_RATE = 0.05;
export const DEFAULT_SELLER_COMMISSION_RATE = 0.05;
export const DEFAULT_REFERRAL_RATE = 0.02;

interface CommissionRates {
    buyer: number;
    seller: number;
    referral: number;
}

/**
 * Calculate commission breakdown for a sale
 * @param price - Sale price
 * @param hasReferrer - Whether buyer has a referrer
 */
/**
 * Calculate commission breakdown for a sale
 * @param price - Sale price (Listing Price that seller sets)
 * @param hasReferrer - Whether buyer has a referrer
 * @param rates - Dynamic rates from SystemConfig
 */
export const calculateCommissionBreakdown = (
    price: number,
    hasReferrer: boolean = false,
    rates: CommissionRates = { buyer: DEFAULT_BUYER_COMMISSION_RATE, seller: DEFAULT_SELLER_COMMISSION_RATE, referral: DEFAULT_REFERRAL_RATE }
) => {
    const VAT_RATE = 0.20; // ⚖️ Turkey VAT %20
    const STOPAJ_RATE = 0.01; // ⚖️ Intermediary Withholding %1 (2025 Decision 9284)

    // 1. Commission Calculations (KDV Hariç)
    const netBuyerCommission = price * rates.buyer;
    const netSellerCommission = price * rates.seller;

    // 2. VAT (KDV) on commissions
    const buyerCommissionVat = netBuyerCommission * VAT_RATE;
    const sellerCommissionVat = netSellerCommission * VAT_RATE;
    const totalCommissionVat = buyerCommissionVat + sellerCommissionVat;

    // 3. Buyer side
    const buyerCommission = netBuyerCommission + buyerCommissionVat;
    const buyerPays = price + buyerCommission;

    // 4. Seller side (Pre-Stopaj)
    const sellerCommission = netSellerCommission + sellerCommissionVat;
    const sellerGrossPayout = price - sellerCommission;

    // 5. Stopaj (Withholding) - Calculated on net payout to seller
    // ⚖️ Intermediary must withhold %1 of the payment to the service provider
    const stopajAmount = sellerGrossPayout * STOPAJ_RATE;
    const sellerReceives = sellerGrossPayout - stopajAmount;

    // 6. Platform Revenue
    const totalCommission = netBuyerCommission + netSellerCommission;
    const referralReward = hasReferrer ? price * rates.referral : 0;
    const platformEarnings = totalCommission - referralReward;

    return {
        productPrice: price,
        buyerCommission: netBuyerCommission,
        sellerCommission: netSellerCommission,
        commissionVat: totalCommissionVat,
        stopajAmount,
        sellerNetPayout: sellerReceives,
        totalPaidByBuyer: buyerPays,

        // Legacy fields mapping
        buyerPays,
        sellerReceives,
        platformEarnings,
        referralReward
    };
};

// Legacy functions for backward compatibility
export const calculateCommission = (price: number): { amount: number, rate: number } => {
    const rate = DEFAULT_SELLER_COMMISSION_RATE;
    const amount = price * rate;
    return { amount, rate };
};

export const calculateReferralReward = (price: number): number => {
    return price * DEFAULT_REFERRAL_RATE;
};
